import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import authService from '../../services/keycloak/authService';
import './ForgotPassword.css';

function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [expirationTime, setExpirationTime] = useState('1 hour');

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setError('');
  };

  // Fetch expiration time from backend on component mount
  React.useEffect(() => {
    const fetchExpirationTime = async () => {
      try {
        const response = await fetch('/api/auth/email-token-lifespan');
        const data = await response.json();
        if (data.success && data.data.displayText) {
          setExpirationTime(data.data.displayText);
        }
      } catch (err) {
        console.error('Failed to fetch expiration time:', err);
        // Keep default value if fetch fails
      }
    };

    fetchExpirationTime();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // Validate email
      if (!email.trim()) {
        setError('Please enter your email address');
        setIsLoading(false);
        return;
      }

      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setError('Please enter a valid email address');
        setIsLoading(false);
        return;
      }

      // Send password reset request to backend
      const response = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to send reset email');
      }

      // Show success message
      setSubmitted(true);
      setIsLoading(false);
    } catch (err) {
      setError(err.message || 'Failed to process password reset. Please try again.');
      console.error('Password reset error:', err);
      setIsLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="auth-container">
        <button className="back-button-circle" onClick={() => navigate('/login')} type="button">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12.5 15L7.5 10L12.5 5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <div className="auth-card">
          <div className="auth-header">
            <h1>Check Your Email</h1>
            <p>Password reset instructions sent</p>
          </div>

          <div className="success-message">
            <div className="success-icon">✓</div>
            <p>
              We've sent password reset instructions to <strong>{email}</strong>
            </p>
            <p>
              Please check your email and follow the link to reset your password. The link will
              expire in {expirationTime}.
            </p>
          </div>

          <div className="auth-footer">
            <p>
              Didn't receive the email?{' '}
              <button
                onClick={() => {
                  setSubmitted(false);
                  setEmail('');
                }}
                className="auth-link-button"
              >
                Try again
              </button>
            </p>
          </div>

          <div className="auth-footer">
            <p>
              <Link to="/login" className="auth-link">
                Back to sign in
              </Link>
            </p>
          </div>
        </div>

        <div className="auth-background">
          <div className="auth-decoration decoration-1"></div>
          <div className="auth-decoration decoration-2"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <button className="back-button-circle" onClick={() => navigate('/login')} type="button">
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12.5 15L7.5 10L12.5 5"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <div className="auth-card">
        <div className="auth-header">
          <h1>Reset Password</h1>
          <p>Enter your email to receive reset instructions</p>
        </div>

        {error && <div className="auth-error">{error}</div>}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={handleEmailChange}
              placeholder="Enter your email address"
              disabled={isLoading}
              autoComplete="email"
              autoFocus
            />
          </div>

          <button type="submit" className="auth-button primary" disabled={isLoading}>
            {isLoading ? 'Sending...' : 'Send Reset Instructions'}
          </button>
        </form>

        <div className="auth-divider"></div>
      </div>

      <div className="auth-background">
        <div className="auth-decoration decoration-1"></div>
        <div className="auth-decoration decoration-2"></div>
      </div>
    </div>
  );
}

export default ForgotPassword;
